import { useMemo } from 'react'

import { Descriptions, DescriptionsProps, Typography } from 'antd'
import { createStyles } from 'antd-style'
import { isNil } from 'lodash'

import { BasicProps } from '@eastcoal/common/types/component'
import { pretty } from '@eastcoal/common/utils/decimal'
import { pick } from '@eastcoal/common/utils/object'

import { BizType } from '@eastcoal/shared/enums/BizType'
import {
  DealPriceType,
  DealPriceTypeTextMap
} from '@eastcoal/shared/enums/DealPriceType'
import {
  DepositMethod,
  DepositMethodTemplateMap
} from '@eastcoal/shared/enums/DepositMethod'

import { useAuctionDetailStore } from '../../Store/hooks/useStore'

import { PropertyRightRender } from './PropertyRightRender'
import { StartOrLimitPriceRender } from './StartOrLimitPriceRender'

const useStyles = createStyles(({ prefixCls, token }) => ({
  descriptions: {
    paddingInline: token.padding,
    paddingBlockStart: token.padding,
    paddingBlockEnd: token.paddingXXS,
    borderRadius: token.borderRadiusSM,
    border: `1px solid ${token.colorBorderSecondary}`,
    [`& .${prefixCls}-descriptions-item-label`]: {
      whiteSpace: 'nowrap'
    },
    [`& .${prefixCls}-descriptions-item-content`]: {
      overflow: 'hidden'
    },
    [`& .${prefixCls}-descriptions-row > td `]: {
      paddingBlockEnd: token.paddingSM
    }
  },
  danger: {
    color: token.colorErrorText
  },
  secondary: {
    color: token.colorTextTertiary
  }
}))

const connect = (value: string | number | undefined, ...restArgs: string[]) => {
  return isNil(value) ? '-' : `${value}${restArgs.join('')}`
}

export const AuctionDetailBidRuleCard = ({ className, style }: BasicProps) => {
  const { cx, styles } = useStyles()

  const { detailData, priceInfo, isBidOnlyOnce } = useAuctionDetailStore(
    state => pick(state, ['detailData', 'priceInfo', 'isBidOnlyOnce'])
  )

  const bidOnlyOnce = isBidOnlyOnce()

  const items = useMemo(() => {
    const {
      bizType,
      kindName,
      productName,
      productProperties = [],
      quantity = 0,
      quantityUnit = '',
      splitCount = 1,
      priceUnit,
      minAddPrice = 0,
      maxAddPrice = 0,
      deliveryPlaceName,
      depositValue = 0,
      depositMethod = DepositMethod.SESSION_FIXED,
      dealCount,
      dealPriceType = DealPriceType.HIGHEST,
      remark
    } = detailData

    const addPriceRangeText = minAddPrice + '~' + maxAddPrice + priceUnit

    let items: DescriptionsProps['items'] = []

    if (bizType === BizType.HOUSE_AUCTION) {
      const type = productProperties.find(item => item.name === 'type')?.value

      const buildingArea = productProperties.find(
        item => item.name === 'buildingArea'
      )?.value

      const address = productProperties.find(
        item => item.name === 'address'
      )?.value

      const propertyRight = productProperties.find(
        item => item.name === 'propertyRight'
      )?.value

      items = [
        {
          label: '类型',
          children: type
        },
        {
          label: '标的物',
          children: productName
        },
        {
          label: '建筑面积',
          children: connect(buildingArea, '㎡')
        },
        {
          label: priceInfo.allowLabel,
          children: priceInfo.allowValueText
        },
        {
          label: '起拍价',
          children: <StartOrLimitPriceRender />
        },
        {
          label: '加价幅度',
          children: addPriceRangeText
        },
        {
          label: '地址',
          children: connect(address, '')
        },
        {
          label: '产权',
          children: <PropertyRightRender value={propertyRight} />
        },
        {
          label: '备注',
          children: connect(remark, '')
        }
      ]
    } else {
      const quantityText = connect(quantity, quantityUnit)
      const quantityPreSplitText = connect(quantity / splitCount, quantityUnit)
      const splitCountText = (
        <>
          <span>{splitCount}份</span>
          <span className={styles.secondary}>
            （1份={quantityPreSplitText}）
          </span>
        </>
      )
      const deopsitText = DepositMethodTemplateMap[depositMethod]({
        value: pretty(depositValue)
      })
      const addPriceItem = bidOnlyOnce
        ? []
        : [
            {
              label: '加价幅度',
              children: addPriceRangeText
            }
          ]

      items = [
        {
          label: '品种',
          children: kindName
        },
        {
          label: '数量',
          children: quantityText
        },
        {
          label: '拆分份数',
          children: splitCountText
        },
        {
          label: priceInfo.allowLabel,
          children: priceInfo.allowValueText
        },
        {
          label: priceInfo.label,
          children: <StartOrLimitPriceRender />
        },
        ...addPriceItem,
        {
          label: '交货地',
          children: connect(deliveryPlaceName, '')
        },
        {
          label: '保证金',
          children: deopsitText
        }
      ]
      if (bizType === BizType.RANKING_AUCTION) {
        items.push(
          {
            label: '成交方式',
            children: DealPriceTypeTextMap[dealPriceType]
          },
          {
            label: '可成交人数',
            children: dealCount
          }
        )
      }
    }

    // 防止文字溢出
    items.forEach(item => {
      item.children = (
        <Typography.Text
          ellipsis={{
            tooltip: true
          }}
        >
          {item.children}
        </Typography.Text>
      )
    })

    return items
  }, [detailData, priceInfo, bidOnlyOnce, styles])

  return (
    <Descriptions
      className={cx(styles.descriptions, className)}
      column={3}
      items={items}
      style={style}
    />
  )
}
